import { useCallback, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Blocks, ListChecks, SlidersHorizontal } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { ActionsEditorDialog } from './ActionsEditorDialog'
import { fetchWorkflows, AUTO_STR as S, type WorkflowRow } from './automationApi'

/**
 * A26.03 — Условия и действия (conditions & actions per workflow). Each row maps a
 * workflow's CONDITION (its trigger/event) to its ACTIONS; the row editor opens the
 * real action-pipeline editor (`PUT .../actions`). Placeholder permission USERS_VIEW.
 */
export function ConditionsActionsScreen() {
  const [editing, setEditing] = useState<WorkflowRow | null>(null)
  const [reloadKey, setReloadKey] = useState(0)

  const columns = useMemo<AdminColumn<WorkflowRow>[]>(
    () => [
      { key: 'name', header: S.name, type: 'text' },
      { key: 'trigger', header: S.condition, type: 'text' },
      { key: 'actions', header: S.actions, type: 'number' },
      { key: 'enabled', header: S.enabled, type: 'text' },
      { key: 'runs', header: S.runs, type: 'number' },
    ],
    [],
  )

  const rowActions = useMemo<RowAction<WorkflowRow>[]>(
    () => [
      {
        id: 'edit',
        label: S.editActions,
        icon: <SlidersHorizontal size={14} />,
        onSelect: (r) => setEditing(r),
      },
    ],
    [],
  )

  const fetcher = useCallback(
    (p: Parameters<typeof fetchWorkflows>[0]) => {
      void reloadKey
      return fetchWorkflows(p)
    },
    [reloadKey],
  )

  return (
    <>
      <PageHeader
        title={S.conditions}
        screenId="A26.03"
        actions={
          <Link
            to="/automation/builder"
            className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
          >
            <Blocks size={13} />
            {S.builder}
          </Link>
        }
      />
      <DataTable<WorkflowRow>
        screenId="A26.03"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        exportable
        emptyState={{ icon: <ListChecks size={28} strokeWidth={1.5} /> }}
      />
      <ActionsEditorDialog
        workflow={editing}
        open={editing !== null}
        onClose={() => setEditing(null)}
        onSaved={() => setReloadKey((k) => k + 1)}
      />
    </>
  )
}
